/**
 * Host implementation of upstream `store/analyticsStore` — see ../../README.md.
 *
 * The vendored tree uses exactly two entry points:
 *   analyticsStore(s => s.trackEvent)   (CatalogPricing, as a hook)
 *   analyticsStore.getState().trackEvent (influencer-referral/client, rootStore)
 * so this is a zustand store with the same shape, forwarding to this repo's
 * RudderStack transport instead of upstream's queued loader stack.
 */

import { AnalyticsType } from '@invideoio/web-shared/types/Analytics';
import { Analytics } from '@invideoio/web-shared/utils/analytics';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import { create } from 'zustand';

export interface AnalyticsEvent {
  eventName: string;
  eventData?: Record<
    string,
    string | boolean | number | Record<string, unknown>
  >;
  /**
   * Upstream flushes on a priority queue. This repo sends straight through, so
   * the field is accepted and ignored rather than dropped from the type — the
   * vendored code sets it and must keep compiling.
   */
  priority?: 'high' | 'normal' | 'low';
}

interface AnalyticsStoreState {
  trackEvent: (event: AnalyticsEvent) => void;
}

/**
 * Same treatment `src/utils/analytics.ts#sendRudderStackAnalyticEvent` gives
 * every other event in this app (common properties, lower-cased keys). It is
 * not reused directly because its parameter is a closed union of this repo's
 * four event shapes and the vendored section emits its own event names.
 */
function send(event: AnalyticsEvent) {
  return Analytics.send(AnalyticsType.RUDDER_STACK, {
    eventName: event.eventName,
    eventProperties: {
      ...AnalyticsUtils.commonRudderStackEventProperties(),
      ...AnalyticsUtils.toLowerCaseObject((event.eventData as any) ?? {}),
    },
    userProperties: {},
  });
}

export const analyticsStore = create<AnalyticsStoreState>(() => ({
  trackEvent: (event: AnalyticsEvent) => {
    try {
      send(event);
    } catch (error) {
      console.error('Failed to track pricing-glass event:', error);
    }
  },
}));
